import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { FaGraduationCap, FaGlobe, FaClock, FaDollarSign, FaStar, FaChevronLeft, FaChevronRight, FaCheckCircle, FaBookOpen, FaEnvelope, FaPhoneAlt, FaMapMarkerAlt } from 'react-icons/fa';
import { Link } from 'react-router-dom';

interface TeacherApplication {
  basicInfo: {
    fullName: string;
    email: string;
    country: string;
    languages: string[];
    profilePicture?: string;
  };
  teachingExperience: {
    yearsTeaching: number;
    certifications?: string[];
    resume?: string;
  };
  teachingOffer: {
    teachingStyle: string;
    targetStudents: string[];
    preferredSchedule: {
      days: string[];
      timeSlots: string[];
      timezone: string;
    };
  };
  rates: {
    hourlyRate: number;
    currency: string;
    offersFreeTrial: boolean;
  };
  demoLesson?: {
    videoUrl?: string;
    description?: string;
  };
}

const steps = [
  'Intro',
  'Basic Info',
  'Experience',
  'Offer',
  'Rates',
  'Demo Lesson'
];

const Stepper = ({ currentStep }: { currentStep: number }) => (
  <div className="flex items-center justify-center mb-10">
    {steps.map((step, idx) => (
      <div key={step} className="flex items-center">
        <div className={`flex items-center justify-center w-8 h-8 rounded-full font-bold text-sm transition-all duration-300
          ${currentStep === idx + 1 ? 'bg-skyBlue text-deepIndigo shadow-lg' : 'bg-dustyLavender text-deepIndigo'}`}
        >
          {currentStep > idx + 1 ? <FaCheckCircle className="text-soft-green" /> : idx + 1}
        </div>
        {idx < steps.length - 1 && <div className="w-8 h-1 bg-mutedPeriwinkle mx-2 rounded" />}
      </div>
    ))}
  </div>
);

const BecomeTeacher: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [application, setApplication] = useState<TeacherApplication>({
    basicInfo: {
      fullName: '',
      email: '',
      country: '',
      languages: []
    },
    teachingExperience: {
      yearsTeaching: 0
    },
    teachingOffer: {
      teachingStyle: '',
      targetStudents: [],
      preferredSchedule: {
        days: [],
        timeSlots: [],
        timezone: 'UTC'
      }
    },
    rates: {
      hourlyRate: 0,
      currency: 'USD',
      offersFreeTrial: false
    }
  });
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const targetStudentOptions = [
    'kids', 'adults', 'travelers', 'test-prep', 'business', 'academic'
  ];

  const dayOptions = [
    'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'
  ];

  const timeSlotOptions = [
    '09:00-10:00', '10:00-11:00', '11:00-12:00', '12:00-13:00',
    '13:00-14:00', '14:00-15:00', '15:00-16:00', '16:00-17:00',
    '17:00-18:00', '18:00-19:00', '19:00-20:00', '20:00-21:00'
  ];

  const handleInputChange = (section: keyof TeacherApplication, field: string, value: any) => {
    setApplication(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value
      }
    }));
  };

  const handleArrayChange = (section: keyof TeacherApplication, field: string, value: string, checked: boolean) => {
    setApplication(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: checked 
          ? [...(prev[section] as any)[field], value]
          : (prev[section] as any)[field].filter((item: string) => item !== value)
      }
    }));
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    setErrorMessage(null);
    setSuccessMessage(null);
    try {
      const endpoint = '/api/teachers/apply-or-register';
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(user && { 'Authorization': `Bearer ${localStorage.getItem('authToken')}` })
        },
        body: JSON.stringify(application)
      });
      const result = await response.json();
      if (response.ok) {
        setSuccessMessage(result.message || 'Application submitted! Check your email for details.');
        setCurrentStep(1);
      } else {
        if (result && result.error) {
          setErrorMessage(result.error);
        } else if (response.status === 400) {
          setErrorMessage('Some required information is missing or you already have an application.');
        } else if (response.status === 401 || response.status === 403) {
          setErrorMessage('You are not authorized. Please log in again.');
        } else {
          setErrorMessage('Failed to submit application. Please try again later.');
        }
      }
    } catch (error) {
      setErrorMessage('Network error. Please check your connection and try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderIntroSection = () => (
    <div className="text-center py-16 bg-gradient-to-br from-blue-50 to-indigo-100">
      <div className="max-w-4xl mx-auto px-4">
        <FaGraduationCap className="text-6xl text-indigo-600 mx-auto mb-6" />
        <h1 className="text-4xl font-bold text-gray-900 mb-6">
          Teach Persian. Inspire Learners.
        </h1>
        <p className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto">
          Want to help others learn Farsi while earning on your own terms? Join our teaching network! 
          Apply now to create your teacher profile, set your rates, and connect with students around the world.
        </p>
        
        <div className="grid md:grid-cols-3 gap-8 mb-12">
          <div className="bg-white p-6 rounded-lg shadow-md">
            <FaGlobe className="text-3xl text-indigo-500 mx-auto mb-4" />
            <h3 className="text-lg font-semibold mb-2">Global Reach</h3>
            <p className="text-gray-600">Connect with students from around the world</p>
          </div>
          <div className="bg-white p-6 rounded-lg shadow-md">
            <FaClock className="text-3xl text-indigo-500 mx-auto mb-4" />
            <h3 className="text-lg font-semibold mb-2">Flexible Schedule</h3>
            <p className="text-gray-600">Teach when it works for you</p>
          </div>
          <div className="bg-white p-6 rounded-lg shadow-md">
            <FaDollarSign className="text-3xl text-indigo-500 mx-auto mb-4" />
            <h3 className="text-lg font-semibold mb-2">Set Your Rates</h3>
            <p className="text-gray-600">Earn what you're worth</p>
          </div>
        </div>

        <button
          onClick={() => setCurrentStep(2)}
          className="bg-orange-600 text-white border border-orange-700 shadow-lg font-bold rounded-lg px-6 py-3 hover:bg-orange-700 focus:bg-orange-800 focus:outline-none transition-all duration-200"
        >
          Apply to Teach
        </button>
      </div>
    </div>
  );

  const renderBasicInfo = () => (
    <div className="max-w-2xl mx-auto p-6 ltr" dir="ltr">
      <h2 className="text-3xl font-bold text-deepIndigo mb-4">Basic Information</h2>
      
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2 ltr" dir="ltr">
            Full Name *
          </label>
          <input
            type="text"
            value={application.basicInfo.fullName}
            onChange={(e) => handleInputChange('basicInfo', 'fullName', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 text-gray-900 ltr" dir="ltr"
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2 ltr" dir="ltr">
            Email *
          </label>
          <input
            type="email"
            value={application.basicInfo.email}
            onChange={(e) => handleInputChange('basicInfo', 'email', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 text-gray-900 ltr" dir="ltr"
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2 ltr" dir="ltr">
            Country of Residence *
          </label>
          <input
            type="text"
            value={application.basicInfo.country}
            onChange={(e) => handleInputChange('basicInfo', 'country', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 text-gray-900 ltr" dir="ltr"
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2 ltr" dir="ltr">
            Languages Spoken *
          </label>
          <input
            type="text"
            placeholder="e.g., Persian, English, Arabic"
            value={application.basicInfo.languages.join(', ')}
            onChange={(e) => handleInputChange('basicInfo', 'languages', e.target.value.split(',').map(lang => lang.trim()))}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 text-gray-900 ltr" dir="ltr"
            required
          />
        </div>
      </div>

      <div className="flex justify-between mt-8">
        <button
          onClick={() => setCurrentStep(1)}
          className="flex items-center gap-2 px-6 py-3 rounded-lg bg-orange-600 text-white border border-orange-700 shadow-lg font-bold hover:bg-orange-700 focus:bg-orange-800 focus:outline-none transition-all duration-200"
        >
          <FaChevronLeft /> Back
        </button>
        <button
          onClick={() => setCurrentStep(3)}
          className="flex items-center gap-2 px-6 py-3 rounded-lg bg-orange-600 text-white border border-orange-700 shadow-lg font-bold hover:bg-orange-700 focus:bg-orange-800 focus:outline-none transition-all duration-200"
        >
          Next <FaChevronRight />
        </button>
      </div>
    </div>
  );

  const renderTeachingExperience = () => (
    <div className="max-w-2xl mx-auto p-6 ltr" dir="ltr">
      <h2 className="text-3xl font-bold text-deepIndigo mb-4">Teaching Experience</h2>
      
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2 ltr" dir="ltr">
            How long have you been teaching Persian? (years) *
          </label>
          <input
            type="number"
            min="0"
            value={application.teachingExperience.yearsTeaching}
            onChange={(e) => handleInputChange('teachingExperience', 'yearsTeaching', parseInt(e.target.value) || 0)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 text-gray-900 ltr" dir="ltr"
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2 ltr" dir="ltr">
            Teaching Certifications (optional)
          </label>
          <input
            type="text"
            placeholder="e.g., TEFL, CELTA, Persian Language Teaching Certificate"
            value={application.teachingExperience.certifications?.join(', ') || ''}
            onChange={(e) => handleInputChange('teachingExperience', 'certifications', e.target.value.split(',').map(cert => cert.trim()))}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 text-gray-900 ltr" dir="ltr"
          />
        </div>
      </div>

      <div className="flex justify-between mt-8">
        <button
          onClick={() => setCurrentStep(2)}
          className="flex items-center gap-2 px-6 py-3 rounded-lg bg-orange-600 text-white border border-orange-700 shadow-lg font-bold hover:bg-orange-700 focus:bg-orange-800 focus:outline-none transition-all duration-200"
        >
          <FaChevronLeft /> Back
        </button>
        <button
          onClick={() => setCurrentStep(4)}
          className="flex items-center gap-2 px-6 py-3 rounded-lg bg-orange-600 text-white border border-orange-700 shadow-lg font-bold hover:bg-orange-700 focus:bg-orange-800 focus:outline-none transition-all duration-200"
        >
          Next <FaChevronRight />
        </button>
      </div>
    </div>
  );

  const renderTeachingOffer = () => (
    <div className="max-w-2xl mx-auto p-6 ltr" dir="ltr">
      <h2 className="text-3xl font-bold text-deepIndigo mb-4">Your Teaching Offer</h2>
      
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2 ltr" dir="ltr">
            Describe your teaching style *
          </label>
          <textarea
            value={application.teachingOffer.teachingStyle}
            onChange={(e) => handleInputChange('teachingOffer', 'teachingStyle', e.target.value)}
            rows={4}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 text-gray-900 ltr" dir="ltr"
            placeholder="Describe your approach to teaching Persian..."
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2 ltr" dir="ltr">
            What kind of students do you want to teach? *
          </label>
          <div className="grid grid-cols-2 gap-2">
            {targetStudentOptions.map(option => (
              <label key={option} className="flex items-center">
                <input
                  type="checkbox"
                  checked={application.teachingOffer.targetStudents.includes(option)}
                  onChange={(e) => handleArrayChange('teachingOffer', 'targetStudents', option, e.target.checked)}
                  className="mr-2"
                />
                <span className="capitalize">{option}</span>
              </label>
            ))}
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2 ltr" dir="ltr">
            Preferred Schedule - Days *
          </label>
          <div className="grid grid-cols-2 gap-2">
            {dayOptions.map(day => (
              <label key={day} className="flex items-center">
                <input
                  type="checkbox"
                  checked={application.teachingOffer.preferredSchedule.days.includes(day)}
                  onChange={(e) => {
                    const newSchedule = { ...application.teachingOffer.preferredSchedule };
                    if (e.target.checked) {
                      newSchedule.days.push(day);
                    } else {
                      newSchedule.days = newSchedule.days.filter(d => d !== day);
                    }
                    handleInputChange('teachingOffer', 'preferredSchedule', newSchedule);
                  }}
                  className="mr-2"
                />
                <span className="capitalize">{day}</span>
              </label>
            ))}
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2 ltr" dir="ltr">
            Time Zone *
          </label>
          <select
            value={application.teachingOffer.preferredSchedule.timezone}
            onChange={(e) => handleInputChange('teachingOffer', 'preferredSchedule', { ...application.teachingOffer.preferredSchedule, timezone: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 text-gray-900 ltr" dir="ltr"
          >
            <option value="UTC">UTC</option>
            <option value="EST">Eastern Time (EST)</option>
            <option value="PST">Pacific Time (PST)</option>
            <option value="CET">Central European Time (CET)</option>
            <option value="JST">Japan Standard Time (JST)</option>
          </select>
        </div>
      </div>

      <div className="flex justify-between mt-8">
        <button
          onClick={() => setCurrentStep(3)}
          className="flex items-center gap-2 px-6 py-3 rounded-lg bg-orange-600 text-white border border-orange-700 shadow-lg font-bold hover:bg-orange-700 focus:bg-orange-800 focus:outline-none transition-all duration-200"
        >
          <FaChevronLeft /> Back
        </button>
        <button
          onClick={() => setCurrentStep(5)}
          className="flex items-center gap-2 px-6 py-3 rounded-lg bg-orange-600 text-white border border-orange-700 shadow-lg font-bold hover:bg-orange-700 focus:bg-orange-800 focus:outline-none transition-all duration-200"
        >
          Next <FaChevronRight />
        </button>
      </div>
    </div>
  );

  const renderRates = () => (
    <div className="max-w-2xl mx-auto p-6 ltr" dir="ltr">
      <h2 className="text-3xl font-bold text-deepIndigo mb-4">Set Your Rates</h2>
      
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2 ltr" dir="ltr">
            Hourly Rate (USD) *
          </label>
          <div className="relative">
            <span className="absolute left-3 top-2 text-gray-500">$</span>
            <input
              type="number"
              min="0"
              step="0.01"
              value={application.rates.hourlyRate}
              onChange={(e) => handleInputChange('rates', 'hourlyRate', parseFloat(e.target.value) || 0)}
              className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 text-gray-900 ltr" dir="ltr"
              required
            />
          </div>
        </div>

        <div>
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={application.rates.offersFreeTrial}
              onChange={(e) => handleInputChange('rates', 'offersFreeTrial', e.target.checked)}
              className="mr-2"
            />
            <span className="text-sm font-medium text-gray-700">
              Offer a free trial session (15 minutes)
            </span>
          </label>
        </div>
      </div>

      <div className="flex justify-between mt-8">
        <button
          onClick={() => setCurrentStep(4)}
          className="flex items-center gap-2 px-6 py-3 rounded-lg bg-orange-600 text-white border border-orange-700 shadow-lg font-bold hover:bg-orange-700 focus:bg-orange-800 focus:outline-none transition-all duration-200"
        >
          <FaChevronLeft /> Back
        </button>
        <button
          onClick={() => setCurrentStep(6)}
          className="flex items-center gap-2 px-6 py-3 rounded-lg bg-orange-600 text-white border border-orange-700 shadow-lg font-bold hover:bg-orange-700 focus:bg-orange-800 focus:outline-none transition-all duration-200"
        >
          Next <FaChevronRight />
        </button>
      </div>
    </div>
  );

  const renderDemoLesson = () => (
    <div className="max-w-2xl mx-auto p-6 ltr" dir="ltr">
      <h2 className="text-3xl font-bold text-deepIndigo mb-4">Demo Lesson (Optional)</h2>
      
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2 ltr" dir="ltr">
            Video Introduction URL
          </label>
          <input
            type="url"
            placeholder="YouTube or video link"
            value={application.demoLesson?.videoUrl || ''}
            onChange={(e) => handleInputChange('demoLesson', 'videoUrl', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 text-gray-900 ltr" dir="ltr"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2 ltr" dir="ltr">
            Video Description
          </label>
          <textarea
            value={application.demoLesson?.description || ''}
            onChange={(e) => handleInputChange('demoLesson', 'description', e.target.value)}
            rows={3}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 text-gray-900 ltr" dir="ltr"
            placeholder="Brief description of your demo lesson..."
          />
        </div>
      </div>

      <div className="flex justify-between mt-8">
        <button
          onClick={() => setCurrentStep(5)}
          className="flex items-center gap-2 px-6 py-3 rounded-lg bg-orange-600 text-white border border-orange-700 shadow-lg font-bold hover:bg-orange-700 focus:bg-orange-800 focus:outline-none transition-all duration-200"
        >
          <FaChevronLeft /> Back
        </button>
        <button
          onClick={handleSubmit}
          disabled={isSubmitting}
          className="flex items-center gap-2 px-6 py-3 rounded-lg bg-orange-600 text-white border border-orange-700 shadow-lg font-bold hover:bg-orange-700 focus:bg-orange-800 focus:outline-none transition-all duration-200 disabled:opacity-50"
        >
          {isSubmitting ? 'Submitting...' : 'Submit Application'}
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-paleLilac">
      <div className="max-w-2xl mx-auto bg-white rounded-2xl shadow-2xl p-8 relative animate-fade-in-up">
        <Stepper currentStep={currentStep} />
        {successMessage && (
          <div className="mb-6 p-4 bg-green-100 border border-green-300 text-green-800 rounded-lg text-center font-semibold">
            {successMessage}
          </div>
        )}
        {errorMessage && (
          <div className="mb-6 p-4 bg-red-100 border border-red-300 text-red-700 rounded-lg text-center font-semibold">
            {errorMessage}
          </div>
        )}
        {currentStep === 1 && renderIntroSection()}
        {currentStep === 2 && renderBasicInfo()}
        {currentStep === 3 && renderTeachingExperience()}
        {currentStep === 4 && renderTeachingOffer()}
        {currentStep === 5 && renderRates()}
        {currentStep === 6 && renderDemoLesson()}
      </div>
      <footer className="bg-gray-900 text-white py-12 mt-12">
  <div className="container mx-auto px-4">
    <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
      <div>
        <div className="flex items-center space-x-2 mb-4">
          <FaBookOpen className="text-3xl gradient-text" />
          <h3 className="text-2xl font-bold">Pirooz</h3>
        </div>
        <p className="text-gray-400">Your gateway to Persian language and culture. Learn Farsi while discovering Iran's rich heritage with Pirooz.</p>
      </div>
      <div>
        <h4 className="text-lg font-semibold mb-4">Quick Links</h4>
        <ul className="space-y-2">
          <li><Link to="/" className="text-gray-400 hover:text-white transition">Home</Link></li>
          <li><Link to="/blog" className="text-gray-400 hover:text-white transition">Blog</Link></li>
          <li><Link to="/pathways" className="text-gray-400 hover:text-white transition">Courses</Link></li>
          <li><Link to="/about" className="text-gray-400 hover:text-white transition">About Us</Link></li>
          <li><Link to="/contact" className="text-gray-400 hover:text-amber-400">Contact</Link></li>
        </ul>
      </div>
      <div>
        <h4 className="text-lg font-semibold mb-4">Categories</h4>
        <ul className="space-y-2">
          <li><Link to="/vocabulary" className="text-gray-400 hover:text-white transition">Language Learning</Link></li>
          <li><Link to="/cultural" className="text-gray-400 hover:text-white transition">Persian History</Link></li>
          <li><Link to="/cultural" className="text-gray-400 hover:text-white transition">Iranian Culture</Link></li>
          <li><Link to="/hafez" className="text-gray-400 hover:text-white transition">Persian Literature</Link></li>
          <li><Link to="/travel-guide" className="text-gray-400 hover:text-white transition">Travel Guides</Link></li>
        </ul>
      </div>
      <div>
        <h4 className="text-lg font-semibold mb-4">Contact Us</h4>
        <ul className="space-y-2">
                          <li className="flex items-center space-x-2">
                  <FaEnvelope className="text-purple-400" />
                  <a href="mailto:support@pingquack.com" className="text-gray-400 hover:text-white transition">support@pingquack.com</a>
                </li>
          <li className="flex items-center space-x-2">
            <FaPhoneAlt className="text-purple-400" />
            <a href="tel:+14245378243" className="text-gray-400 hover:text-white transition">+1 (424) 537-8243</a>
          </li>
          <li className="flex items-center space-x-2">
            <FaMapMarkerAlt className="text-purple-400" />
            <span className="text-gray-400">Laguna Hills, CA</span>
          </li>
        </ul>
      </div>
    </div>
    <div className="border-t border-gray-800 mt-8 pt-8 text-center text-gray-400">
      <p>&copy; 2024 پیروز Pirooz. All rights reserved.</p>
    </div>
  </div>
  <style>{`
    .gradient-text {
      background: linear-gradient(90deg, #8B5CF6, #EC4899);
      -webkit-background-clip: text;
      background-clip: text;
      color: transparent;
    }
  `}</style>
</footer>
    </div>
  );
};

export default BecomeTeacher; 